import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, makeStyles } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { LoadingButton } from '~/modules/common/components';
import { CostOverridesNote } from './CostOverridesNote';

const startIcon = <AddIcon />;

const useStyles = makeStyles(theme => ({
  container: {
    width: '100%'
  },
  addButton: {
    textTransform: 'none',
    padding: theme.spacing(0, 1)
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  }
}));

export const ProjectCostOverridesActionComponent = ({
  onCancel,
  onSave,
  saveable = true,
  isSaving = false,
  validateForm,
  actionComponentCustomProps
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const {
    readOnly = false,
    selectedDimensions = [],
    onAddNewGroup,
    isLocked = false
  } = actionComponentCustomProps ?? {};

  const handleSave = useCallback(async () => {
    if (validateForm) {
      const errors = await validateForm();

      if (Object.keys(errors).length) return;
    }
    onSave();
  }, [onSave, validateForm]);

  const firstDimensionDisplayText = selectedDimensions[0]?.displayText ?? '';
  const showAddButton = !readOnly && selectedDimensions.length > 0;
  const addButtonAriaLabel = formatMessage(
    { id: 'projectCostOverrides.dialog.addCostOverrideAriaLabel' },
    { dimension: firstDimensionDisplayText }
  );

  return (
    <div className={classes.container}>
      {showAddButton ? (
        <Button
          color="primary"
          size="small"
          startIcon={startIcon}
          className={classes.addButton}
          onClick={onAddNewGroup}
          aria-label={addButtonAriaLabel}
          disabled={isLocked}
          data-qe-id="CostOverridesTableAddButton"
        >
          {firstDimensionDisplayText.toUpperCase()}
        </Button>
      ) : null}
      <CostOverridesNote />
      <div className={classes.buttonRow}>
        {readOnly ? (
          <Button onClick={onCancel} data-qe-id="ProjectCostOverridesClose">
            <FormattedMessage id="button.close" />
          </Button>
        ) : (
          <>
            <Button
              onClick={onCancel}
              disabled={isSaving}
              data-qe-id="ProjectCostOverridesCancel"
            >
              <FormattedMessage id="button.cancel" />
            </Button>
            <LoadingButton
              color="primary"
              onClick={handleSave}
              disabled={!saveable || isSaving}
              isLoading={isSaving}
              data-qe-id="ProjectCostOverridesSave"
            >
              <FormattedMessage id="button.save" />
            </LoadingButton>
          </>
        )}
      </div>
    </div>
  );
};

ProjectCostOverridesActionComponent.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saveable: PropTypes.bool,
  isSaving: PropTypes.bool,
  validateForm: PropTypes.func,
  actionComponentCustomProps: PropTypes.object
};

export default ProjectCostOverridesActionComponent;
